import type { Toast, ToastOptions } from '@trustoo/components/ui/toaster/types/toastTypes'

/**
 * How long a toast stays up when the caller sets no `timeout`: long enough to read in passing,
 * short enough to get out of the way. Both channels share it, so a message reads the same whether
 * a modal or the page raised it. A toast that must survive until it is dismissed — one carrying
 * something the user still needs while they act on it — asks for that with `timeout: null`.
 */
export const DEFAULT_TOAST_TIMEOUT = 10000

const generateToastId = () =>
    `${new Date().getTime()}-${Math.random().toString(36).substring(2, 7)}`

/**
 * Fills a caller's options out into a complete toast. Red unless another colour was picked, so a
 * bare `addToast({ msg })` reads as a problem rather than as a silent neutral notice.
 */
export const createToast = (options: ToastOptions): Toast => {
    const resolved: ToastOptions = {
        red: !(options.green || options.orange),
        closeBtn: true,
        ...options,
    }

    return {
        ...resolved,
        // Not `??`: an explicit `null` means "never auto-dismiss" and must survive the default.
        timeout: resolved.timeout === undefined ? DEFAULT_TOAST_TIMEOUT : resolved.timeout,
        id: options.id || generateToastId(),
        msg: options.msg ?? '',
        // Fresh each add so re-adding a stable id restarts the dismiss timer.
        restartKey: generateToastId(),
    }
}
